/**
 * Symmetric encryption: AES-256-GCM (authenticated).
 *
 * Used for: item bodies, item-key wrapping (symKey wraps itemKey), vault-key
 * wrapping (encKey wraps symKey), and private-key wrapping (symKey wraps privKey).
 */
import { fromBytes, fromBase64, packIvAndCiphertext, randomBytes, toBase64Url, toBytes, unpackIvAndCiphertext, type Bytes } from './encoding.js';

export const AES_GCM_IV_LENGTH = 12;

/** Import a 32-byte raw key as an AES-GCM CryptoKey. */
export async function importAesKey(raw: Bytes): Promise<CryptoKey> {
  if (raw.length !== 32) throw new Error(`AES-256 key must be 32 bytes, got ${raw.length}`);
  return crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}

export interface SealedBytes {
  iv: Bytes;
  ciphertext: Bytes;
}

/** Encrypt raw bytes with AES-GCM. Optional AAD binds ciphertext to context. */
export async function encryptBytes(
  key: Bytes,
  plaintext: Bytes,
  aad?: Bytes,
): Promise<SealedBytes> {
  const cryptoKey = await importAesKey(key);
  const iv = randomBytes(AES_GCM_IV_LENGTH);
  const ciphertext = await crypto.subtle.encrypt(
    aad ? { name: 'AES-GCM', iv, additionalData: aad } : { name: 'AES-GCM', iv },
    cryptoKey,
    plaintext,
  );
  return { iv, ciphertext: new Uint8Array(ciphertext) };
}

/** Decrypt raw bytes produced by `encryptBytes`. Throws if the tag is invalid. */
export async function decryptBytes(
  key: Bytes,
  iv: Bytes,
  ciphertext: Bytes,
  aad?: Bytes,
): Promise<Bytes> {
  const cryptoKey = await importAesKey(key);
  const plaintext = await crypto.subtle.decrypt(
    aad ? { name: 'AES-GCM', iv, additionalData: aad } : { name: 'AES-GCM', iv },
    cryptoKey,
    ciphertext,
  );
  return new Uint8Array(plaintext);
}

/** Encrypt a string, returning a single packed `iv||ciphertext` base64url blob. */
export async function sealString(key: Bytes, plaintext: string, aad?: Bytes): Promise<string> {
  const { iv, ciphertext } = await encryptBytes(key, toBytes(plaintext), aad);
  return packIvAndCiphertext(iv, ciphertext);
}

/** Decrypt a packed blob from `sealString`. */
export async function openString(key: Bytes, packed: string, aad?: Bytes): Promise<string> {
  const { iv, ciphertext } = unpackIvAndCiphertext(packed, AES_GCM_IV_LENGTH);
  const plaintext = await decryptBytes(key, iv, ciphertext, aad);
  return fromBytes(plaintext);
}

/** Encrypt a key (bytes) under another key, returning packed base64url. */
export async function wrapKey(wrappingKey: Bytes, keyToWrap: Bytes): Promise<string> {
  const { iv, ciphertext } = await encryptBytes(wrappingKey, keyToWrap);
  return packIvAndCiphertext(iv, ciphertext);
}

/** Unwrap a key (bytes) previously wrapped with `wrapKey`. */
export async function unwrapKey(wrappingKey: Bytes, packed: string): Promise<Bytes> {
  const { iv, ciphertext } = unpackIvAndCiphertext(packed, AES_GCM_IV_LENGTH);
  return decryptBytes(wrappingKey, iv, ciphertext);
}

/** Convenience: base64url encode/decode for callers that just need transport. */
export { toBase64Url, fromBase64 };
